/**
 * Global environment selector (W1b) — the header control that sets the
 * environment scope in the URL, via EnvironmentContext's adapter over
 * ScopeContext.
 *
 * TWO THINGS WERE WRONG HERE, AND ROB SAW BOTH AT ONCE.
 *
 * 1. IT LOOKED SET WHEN NOTHING WAS SET. `useEnvironment().environment` falls
 *    back to localStorage and then to 'dev' so that pages always have a
 *    concrete value to query with. This control highlighted that fallback — so
 *    on a URL carrying no environment at all it rendered a solid TEST while the
 *    scope ribbon two lines below correctly read "All environments ·
 *    read-only". Clicking the already-highlighted segment then did nothing
 *    visible, because the value it appeared to be showing was never the scope
 *    in the first place. It now highlights only a REAL environment in the URL
 *    (hasExplicitEnvironment), and shows "All" the rest of the time.
 *
 * 2. THERE WAS NO WAY BACK. With only four segments there was no control for
 *    the estate-wide read-only scope, so once you picked an environment you
 *    could never return to "all" without hand-editing the URL. "All" is now a
 *    real segment that clears the parameter.
 *
 * The other half of the duplication — the scope ribbon rendering its own
 * second <select> for the same field — is fixed in ScopeRibbon.tsx, which now
 * only DISPLAYS the scope. One control, one indicator.
 */
import React from 'react';
import { useEnvironment, type Environment } from '../contexts/EnvironmentContext';
import { useDirtyGuard } from '../utils/dirtyGuard';

const LABEL: Record<Environment, string> = { dev: 'DEV', test: 'TEST', uat: 'UAT', prod: 'PROD' };

const EnvironmentSelector: React.FC = () => {
  const { environment, setEnvironment, environments, provisioned, hasExplicitEnvironment, clearEnvironment } =
    useEnvironment();
  // The unsaved-form guard came with the control. It used to sit on the scope
  // ribbon's <select>; now that this is the only way to change the environment,
  // dropping it here would silently discard a half-filled form (the Plan
  // environment draft, for one) on any scope switch.
  const guardedChange = useDirtyGuard();

  const segment = (key: string, label: string, active: boolean, enabled: boolean, title: string, onClick: () => void, first: boolean) => (
    <button
      key={key}
      type="button"
      aria-pressed={active}
      disabled={!enabled}
      title={title}
      onClick={() => enabled && guardedChange(onClick)}
      className={[
        'px-3 py-1.5 text-xs font-semibold transition-colors',
        first ? '' : 'border-l border-iflow-900/10',
        active
          ? 'bg-iflow-900 text-white'
          : enabled
            ? 'text-slate-500 hover:text-iflow-800 hover:bg-white'
            : 'text-slate-300 cursor-not-allowed',
      ].join(' ')}
    >
      {label}
    </button>
  );

  return (
    <div
      role="group"
      aria-label="Environment"
      className="hidden sm:flex items-center rounded-lg border border-iflow-900/10 bg-slate-50 overflow-hidden"
    >
      {segment(
        'all',
        'ALL',
        !hasExplicitEnvironment,
        true,
        'Estate-wide, read-only — no environment in the URL',
        clearEnvironment,
        true,
      )}
      {environments.map((env) =>
        segment(
          env,
          LABEL[env],
          // Only ever highlight an environment the URL actually names.
          hasExplicitEnvironment && env === environment,
          provisioned.includes(env),
          provisioned.includes(env) ? `View ${LABEL[env]}` : `${LABEL[env]} — not provisioned`,
          () => setEnvironment(env),
          false,
        ),
      )}
    </div>
  );
};

export default EnvironmentSelector;
